import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UserCheck, Layers } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import PricingCards from "@/components/PricingCards";
import Faq from "@/components/Faq";
import NotifyForm from "@/components/NotifyForm";
import StoreButtons from "@/components/StoreButtons";
import SectionHeading from "@/components/SectionHeading";
import { PRICING } from "@/config/site";
import { usePageMeta } from "@/lib/meta";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { scrollToId } from "@/lib/scroll";

const FAQ_ITEMS = [
  {
    q: "How much does CoachER cost?",
    a: `CoachER is ${PRICING.monthly.display} per month or ${PRICING.annual.display} per year. The annual plan works out to approximately ${PRICING.annual.monthlyEquivalent} per month and saves approximately ${PRICING.annual.savingsPercent}% compared with paying monthly for 12 months.`,
  },
  {
    q: "Does CoachER have a free version?",
    a: `CoachER offers a free trial rather than a permanent free plan. During the trial, coaches can begin experiencing CoachER with up to ${PRICING.trial.playerLimit} players before subscribing.`,
  },
  {
    q: "Do my Assistant Coaches have to pay too?",
    a: "No. An authorized Assistant Coach does not need a separate subscription simply to participate on a subscribed Head Coach's team.",
  },
  {
    q: "What if an Assistant Coach wants their own team?",
    a: "If an Assistant Coach wants to create and own an independent CoachER team, normal CoachER subscription requirements apply.",
  },
  {
    q: "Can I manage multiple teams?",
    a: "Yes. CoachER is designed for coaches who may coach multiple teams or seasons — follow the active-team limits in the app.",
  },
  {
    q: "What happens to my old teams?",
    a: "Historical teams stay available as inactive/archived teams. You never have to delete a previous season just to start the next one.",
  },
  {
    q: "Can I cancel?",
    a: "Yes. Subscription management and cancellation follow the policies of the platform through which the subscription was purchased (Apple App Store or Google Play).",
  },
  {
    q: "Which plan is the better value?",
    a: `The annual subscription is ${PRICING.annual.display}/year, versus ${PRICING.annual.twelveMonthAtMonthly} for 12 months at the monthly price. That saves ${PRICING.annual.savingsAmount} per year, or approximately ${PRICING.annual.savingsPercent}%.`,
  },
];

const Pricing = () => {
  usePageMeta(
    "CoachER Pricing | Your Digital Dugout Assistant",
    "CoachER is $6.99/month or $59/year. Start your free trial and simplify practice planning, team management, lineups, player development and coaching organization.",
    "/pricing"
  );

  useEffect(() => {
    trackEvent(EVENTS.pricingPageView);
  }, []);

  return (
    <div data-testid="pricing-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-coacher-navy pt-32 pb-20 sm:pt-44 sm:pb-24">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-8">
          <p className="flex items-center justify-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-white/70">
            <span className="inline-block h-px w-10 bg-coacher-red" aria-hidden="true" />
            Pricing
            <span className="inline-block h-px w-10 bg-coacher-red" aria-hidden="true" />
          </p>
          <MaskedLines
            lines={["Coach smarter.", "Spend less time planning."]}
            delay={0.15}
            className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl font-semibold uppercase leading-[1.0] tracking-tight text-white"
            lineClassName="[&:nth-child(2)]:text-outline-white"
          />
          <Reveal delay={0.4}>
            <p className="mx-auto mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-white/70">
              CoachER gives youth coaches one digital dugout for planning practices, managing players, building lineups, tracking development, capturing notes, and running a more organized team.
            </p>
            <p className="mt-5 font-display text-lg sm:text-xl font-semibold uppercase tracking-wide text-white">
              Starting at {PRICING.annual.monthlyEquivalent}/month when billed annually.
            </p>
            <button
              data-testid="pricing-hero-cta"
              onClick={() => {
                trackEvent(EVENTS.pricingTrialCtaClicked, { plan: "hero" });
                scrollToId("launch-notify");
              }}
              className="mt-8 inline-flex items-center gap-2 rounded-md bg-coacher-red px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-coacher-red-dark"
            >
              Start Your Free Trial <ArrowRight size={16} />
            </button>
            <p className="mt-4 text-sm text-white/60">
              Try CoachER with up to {PRICING.trial.playerLimit} players before building your full roster.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CARDS */}
      <section className="bg-coacher-surface py-20 sm:py-24 pinstripes">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <PricingCards />
          <Reveal className="mx-auto mt-8 max-w-4xl text-center">
            <p className="text-sm text-coacher-slate">
              Free trial first, then a paid subscription — no permanent free plan. During your trial you can add up to {PRICING.trial.playerLimit} players.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ASSISTANTS + MULTI-TEAM */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div data-testid="pricing-assistants-card" className="h-full rounded-xl border border-coacher-border bg-white p-8 sm:p-10 shadow-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-coacher-navy">
                <UserCheck size={20} className="text-white" />
              </span>
              <h2 className="mt-6 font-display text-2xl sm:text-3xl font-semibold uppercase tracking-tight text-coacher-navy">
                Bring your coaching staff with you
              </h2>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-coacher-slate">
                One coaching staff. One shared team. No need to make every assistant buy CoachER just to help coach.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-coacher-slate">
                The Head Coach maintains the CoachER subscription. Authorized Assistant Coaches join at no extra cost, with access based on the role and permissions the Head Coach grants. If an assistant wants to create and own their own CoachER team, normal subscription requirements apply.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="pricing-multiteam-card" className="h-full rounded-xl border border-coacher-border bg-white p-8 sm:p-10 shadow-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-coacher-navy">
                <Layers size={20} className="text-white" />
              </span>
              <h2 className="mt-6 font-display text-2xl sm:text-3xl font-semibold uppercase tracking-tight text-coacher-navy">
                Coach more than one team? No problem.
              </h2>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-coacher-slate">
                Fall baseball, spring baseball, softball, travel, rec — CoachER is built for coaches who wear more than one hat.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-coacher-slate">
                Past seasons stay with you as inactive teams. You never have to delete an old season just to start the next one.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUE + ANCHOR */}
      <section className="relative overflow-hidden bg-coacher-navy py-20 sm:py-28">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-white/60">Why it's worth it</p>
            <h2 className="mt-5 font-display text-3xl sm:text-5xl font-semibold uppercase leading-[1.05] tracking-tight text-white">
              Built for the coach doing this after work
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-white/70">
              Youth coaches already give their nights, weekends, energy, and often their own money to their teams. CoachER helps reduce the time spent building plans, organizing lineups, tracking notes, and trying to remember everything between practices and games.
            </p>
            <p className="mt-8 font-varsity text-3xl sm:text-4xl text-white">Less admin. More coaching.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div data-testid="pricing-anchor-callout" className="mx-auto mt-12 max-w-xl rounded-xl border border-white/15 bg-white/5 p-7">
              <p className="font-display text-xl font-semibold uppercase tracking-wide text-white">
                Less than $5/month on the annual plan.
              </p>
              <p className="mt-2.5 text-sm leading-relaxed text-white/70">
                For {PRICING.annual.display} per year, CoachER gives coaches a season-to-season digital coaching workspace for approximately {PRICING.annual.monthlyEquivalent}/month.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="pricing-faq" className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <SectionHeading align="center" eyebrow="FAQ" title="Fair questions, straight answers" />
          <div className="mt-12">
            <Faq items={FAQ_ITEMS} />
          </div>
        </div>
      </section>

      {/* BOTTOM CTA + NOTIFY */}
      <section id="launch-notify" className="relative overflow-hidden bg-coacher-navy-deep py-20 sm:py-28">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-8">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-semibold uppercase leading-[1.05] tracking-tight text-white">
              Your team needs a coach.<br />
              <span className="text-outline-white">Your coaching doesn't need more admin.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-white/70">
              Let CoachER handle more of the organization so you can focus on your players.
            </p>
            <p className="mt-7 font-display text-lg font-semibold uppercase tracking-wide text-white">
              {PRICING.monthly.display}/month <span className="mx-2 text-white/40">or</span> {PRICING.annual.display}/year — Best Value
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mx-auto mt-10 max-w-2xl">
            <NotifyForm />
            <Link
              to="/features"
              data-testid="pricing-secondary-cta"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-white/80 underline decoration-coacher-red decoration-2 underline-offset-8 transition-colors hover:text-white"
            >
              See What CoachER Can Do <ArrowRight size={15} />
            </Link>
            <StoreButtons className="mt-9 justify-center" />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
